import { CommonModule } from '@angular/common'
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { RouterModule } from '@angular/router'
import { Store } from '@ngrx/store'
import { InfiniteScrollDirective } from 'ngx-infinite-scroll'
import { Observable, Subscription } from 'rxjs'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { AppState } from 'src/app/ngrx/states'
import { BookmarkService } from 'src/app/service/data/bookmark.service'
import { DdcLoggerService } from 'src/app/service/log/ddc-logger.service'
import { BookmarkGroup, BookmarkStateService } from 'src/app/service/state/bookmark-state.service'
import { DdcEditOneDialogComponent } from 'src/app/shared/ddc/ddc-edit-one-dialog/ddc-edit-one-dialog.component'
import { DdcButtonNOneComponent } from '../../shared/ddc/ddc-button-n-one/ddc-button-n-one.component'
import { DdcCollectionComponent } from '../../shared/ddc/ddc-collection/ddc-collection.component'
import { DdcLoadingSkeletonComponent } from '../../shared/ddc/ddc-loading-skeleton/ddc-loading-skeleton.component'
import { DdcNotAvailableComponent } from '../../shared/ddc/ddc-not-available/ddc-not-available.component'
import { DdcTextGradientComponent } from '../../shared/ddc/ddc-text-gradient/ddc-text-gradient.component'
import { DdcAddDialogThreeComponent } from '../ddc-add-dialog-three/ddc-add-dialog-three.component'
@Component({
  selector: 'ddc-saved',
  templateUrl: './ddc-saved.component.html',
  styleUrl: './ddc-saved.component.scss',
  encapsulation: ViewEncapsulation.Emulated,
  imports: [
    AngularMaterialsModules,
    CommonModule,
    RouterModule,
    DdcTextGradientComponent,
    DdcButtonNOneComponent,
    DdcCollectionComponent,
    DdcNotAvailableComponent,
    InfiniteScrollDirective,
    DdcLoadingSkeletonComponent,
  ],
})
export class DdcSavedComponent implements OnInit, OnDestroy {
  // Observables from state service
  _collections$: Observable<BookmarkGroup[]>
  _isCollectionsLoading$: Observable<boolean>
  _collectionsError$: Observable<string | null>

  // Local state for backwards compatibility with template
  _collections: BookmarkGroup[] = []
  _isCollectionsLoading: boolean = false
  _fetchCollectionsError: string = 'Empty.'
  _isCollectionsEmpty: boolean = false

  // Legacy properties for pagination (can be removed if not needed)
  _isCollectionsPngLoading: boolean = false
  _collectionsScrollLoading: boolean = false
  _collectionsPageInfoLabel = 'Scroll down to load more'
  _collectionsCurrentPage = 1
  _collectionsPerPage = 10
  _nAllCollections: number = 0
  _collectionsEnd: boolean = false
  _collectionsPaginationLoading: boolean = false

  private subscriptions = new Subscription()

  constructor(
    private logger: DdcLoggerService,
    private store: Store<AppState>,
    public dialog: MatDialog,
    public bookmark_service: BookmarkService,
    private bookmarkStateService: BookmarkStateService,
  ) {
    // Initialize observables
    this._collections$ = this.bookmarkStateService.bookmarkGroups$
    this._isCollectionsLoading$ = this.bookmarkStateService.bookmarkGroupsLoading$
    this._collectionsError$ = this.bookmarkStateService.bookmarkGroupsError$
  }

  ngOnInit(): void {
    this.logger.info('DdcSavedComponent: ngOnInit()')

    // Subscribe to observables and update local state for template compatibility
    this.subscriptions.add(
      this._collections$.subscribe(collections => {
        this.logger.info('DdcSavedComponent: Collections updated from state:', collections)
        // Filter out any invalid collections (collections without proper id or name)
        this._collections = collections.filter(
          collection =>
            collection && collection.id && collection.name && collection.name.trim() !== '',
        )
        this._isCollectionsEmpty = this._collections.length === 0
        this.logger.info('DdcSavedComponent: Filtered collections:', this._collections)
      }),
    )

    this.subscriptions.add(
      this._isCollectionsLoading$.subscribe(loading => {
        this._isCollectionsLoading = loading
      }),
    )

    this.subscriptions.add(
      this._collectionsError$.subscribe(error => {
        if (error) {
          this._fetchCollectionsError = error
        }
      }),
    )

    // Load bookmark groups from state service
    this.loadBookmarkGroups()
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe()
  }

  private async loadBookmarkGroups(): Promise<void> {
    try {
      await this.bookmarkStateService.loadBookmarkGroups()
    } catch (error) {
      this.logger.error('DdcSavedComponent: Error loading bookmark groups', error)
    }
  }

  onCollectionsScroll(): void {
    this.logger.info('DdcSavedComponent: onCollectionsScroll()')
    // Scroll functionality can be implemented if needed for pagination
    // For now, this method maintains compatibility with the template
  }

  appendCollectionsData = (): void => {
    this.logger.info('DdcSavedComponent: appendCollectionsData()')
    // Legacy method - functionality moved to state service
    // Can be removed if pagination is not needed
  }

  openAddDialog(targetChip: string): void {
    this.logger.info('DdcSavedComponent: openAddDialog()')
    const dialogRef = this.dialog.open(DdcAddDialogThreeComponent, {
      data: 'collection',
    })

    dialogRef.afterClosed().subscribe(result => {
      // No need to manually add to state service here since the dialog already does it
      // The state service will automatically update the collections through reactive subscriptions
      if (result && result.type === 'collection' && result.element) {
        this.logger.info('DdcSavedComponent: Collection dialog closed successfully', result.element)
      }
    })
  }

  toggleCollectionsPaginationLoading = (): void => {
    this._isCollectionsPngLoading = !this._isCollectionsPngLoading
  }

  // Legacy method - keeping for compatibility but using state service
  getCollectionsPage(): void {
    this.logger.info('DdcSavedComponent: getCollectionsPage() - delegating to state service')
    this.loadBookmarkGroups()
  }

  openEditGroupDialog(group: any): void {
    this.logger.info('DdcSavedComponent: openEditGroupDialog()')
    const dialogRef = this.dialog.open(DdcEditOneDialogComponent, {
      data: {
        element: {
          type: 'collection',
          elem: group,
        },
      },
    })

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'edited') {
        // Refresh the bookmark groups from state service
        this.loadBookmarkGroups()
      }
    })
  }
}
